/**
 * Early Warning Signals API Service
 *
 * This service provides API methods for the EWS dashboard.
 * Uses httpClient for backend integration with token rotation support.
 *
 * API Spec: /FinProAdminServer/docs/ews-api-spec.json
 */

import { httpClient } from './http-client.service';
import {
  EWSCustomer,
  EWSCustomersResponse,
  EWSOverviewMetrics,
  EWSDistributionsData,
  GetCustomersParams,
  GetOverviewMetricsParams,
  GetDistributionsParams,
  EWSView,
  GetEWSBatchesParams,
  EWSBatchesResponse,
  EWSBatchesApiResponse,
  OverviewApiResponse,
  CustomersListApiResponse,
  CustomerDetailApiResponse,
  DistributionsApiResponse,
  RefreshApiResponse,
  FilterOptionsApiResponse,
  EWSFilterOptions,
  mapCustomerListItem,
  mapCustomerDetail,
} from '../types/ews.types';


// =============================================================================
// EWS Config types (mirror of FAS EWSViewConfigResponse minus the etag)
// =============================================================================

/** One indicator: stable code-side value + UI-side label, both DB-sourced. */
export interface EWSConfigIndicatorEntry {
  value: string;
  label: string;
}

export interface EWSConfigViewEntry {
  value: EWSView;
  label: string;
  indicators: EWSConfigIndicatorEntry[];
}

export interface EWSConfigResponse {
  version: string;
  views: EWSConfigViewEntry[];
}

export interface EWSConfigApiResponse {
  status: 'success' | 'error';
  message?: string;
  data?: EWSConfigResponse;
}

// =============================================================================
// API Service Class
// =============================================================================

class EWSApiService {
  /**
   * API endpoint prefix for EWS routes.
   * Base URL is already handled by httpClient.
   */
  private readonly basePath = 'ews';

  /**
   * Fetch overview metrics for the specified time period.
   *
   * @param params - Parameters including period and optional custom dates
   * @returns Overview metrics data
   */
  async getOverviewMetrics(params: GetOverviewMetricsParams): Promise<EWSOverviewMetrics> {
    const queryParams = new URLSearchParams();

    if (params.batchId) {
      queryParams.append('batchId', params.batchId);
    }

    if (params.view) {
      queryParams.append('view', params.view);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.basePath}/overview?${queryString}`
      : `${this.basePath}/overview`;

    const response = await httpClient.get<OverviewApiResponse>(url);

    if (response.data.status !== 'success' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch overview metrics');
    }

    return response.data.data;
  }

  /**
   * Fetch paginated customer list with optional search and filtering.
   *
   * @param params - Parameters including pagination, search, and filters
   * @returns Paginated customer list with pagination info
   */
  async getCustomers(params: GetCustomersParams): Promise<EWSCustomersResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());

    if (params.batchId) {
      queryParams.append('batchId', params.batchId);
    }

    if (params.searchQuery) {
      queryParams.append('searchQuery', params.searchQuery);
    }

    if (params.searchDomain) {
      queryParams.append('searchDomain', params.searchDomain);
    }

    if (params.riskLevel) {
      queryParams.append('riskLevel', params.riskLevel);
    }

    if (params.indicator) {
      queryParams.append('indicator', params.indicator);
    }

    if (params.indicatorLevel) {
      queryParams.append('indicatorLevel', params.indicatorLevel);
    }

    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }

    if (params.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }

    if (params.balanceOperator && params.balanceValue !== undefined) {
      queryParams.append('balanceOperator', params.balanceOperator);
      queryParams.append('balanceValue', params.balanceValue.toString());
    }

    if (params.view) {
      queryParams.append('view', params.view);
    }

    const response = await httpClient.get<CustomersListApiResponse>(
      `${this.basePath}/customers?${queryParams.toString()}`
    );

    if (response.data.status !== 'success' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch customers');
    }

    // Map API response to internal format with legacy field support
    const customers = response.data.data.customers.map(mapCustomerListItem);

    return {
      data: customers,
      pagination: response.data.data.pagination,
      metaColumns: response.data.data.metaColumns,
    };
  }

  /**
   * Fetch details for a specific customer by tracking ID.
   *
   * @param trackingId - Customer tracking ID (consent ID) in UUID format
   * @returns Customer details or null if not found
   */
  async getCustomerDetails(trackingId: string, batchId?: string, view?: EWSView): Promise<EWSCustomer | null> {
    try {
      const qp = new URLSearchParams();
      if (batchId) qp.append('batchId', batchId);
      if (view) qp.append('view', view);
      const queryParams = qp.toString() ? `?${qp.toString()}` : '';
      const response = await httpClient.get<CustomerDetailApiResponse>(
        `${this.basePath}/customers/${trackingId}${queryParams}`
      );

      if (response.data.status !== 'success' || !response.data.data) {
        return null;
      }

      // Map API response to internal format with legacy field support
      return mapCustomerDetail(response.data.data);
    } catch (error: unknown) {
      // Handle 404 gracefully
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Fetch risk indicator distributions for analytics charts.
   *
   * @param params - Parameters including period, specific indicators, and risk level filter
   * @returns Distribution data for all risk indicators
   */
  async getDistributions(params?: GetDistributionsParams): Promise<EWSDistributionsData> {
    const queryParams = new URLSearchParams();

    if (params?.batchId) {
      queryParams.append('batchId', params.batchId);
    }

    if (params?.indicators && params.indicators.length > 0) {
      queryParams.append('indicators', params.indicators.join(','));
    }

    if (params?.riskLevel) {
      queryParams.append('riskLevel', params.riskLevel);
    }

    if (params?.view) {
      queryParams.append('view', params.view);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.basePath}/distributions?${queryString}`
      : `${this.basePath}/distributions`;

    const response = await httpClient.get<DistributionsApiResponse>(url);

    if (response.data.status !== 'success' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch distributions');
    }

    return response.data.data;
  }

  /**
   * Refresh the overview metrics (invalidates cache on backend).
   *
   * @param period - Time period for the refreshed metrics
   * @returns Refreshed overview metrics
   */
  async refreshMetrics(batchId?: string): Promise<EWSOverviewMetrics> {
    // First, invalidate the cache
    const refreshResponse = await httpClient.post<RefreshApiResponse>(
      `${this.basePath}/refresh`,
      {}
    );

    if (refreshResponse.data.status !== 'success') {
      throw new Error(refreshResponse.data.message || 'Failed to refresh metrics');
    }

    // Then fetch fresh metrics
    return this.getOverviewMetrics({ batchId });
  }

  /**
   * Fetch available filter options (consent types) from EWS analytics.
   *
   * @returns Filter options including available consent types
   */
  async getFilterOptions(): Promise<EWSFilterOptions> {
    const response = await httpClient.get<FilterOptionsApiResponse>(
      `${this.basePath}/filter-options`
    );

    if (response.data.status !== 'success' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch filter options');
    }

    return response.data.data;
  }

  /**
   * Export/download a report for EWS data.
   *
   * @param params - Report generation parameters
   * @returns Promise with blob data for download
   */
  async exportReport(params: {
    period: string;
    reportType: 'ews' | 'analytics' | 'raw';
    format: 'xlsx' | 'csv' | 'json';
    batchId?: string;
    consentType?: string;
  }): Promise<Blob> {
    const body: Record<string, any> = {
      period: params.period,
      report_type: params.reportType,
      format: params.format,
    };

    if (params.batchId) {
      body.batch_id = params.batchId;
    }

    if (params.consentType) {
      body.consent_type = params.consentType;
    }

    const response = await httpClient.post(
      `${this.basePath}/export`,
      body,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Fetch paginated list of EWS batches with summary metrics.
   *
   * @param params - Parameters including pagination, search, and status filter
   * @returns Batches list with pagination and summary
   */
  async getBatches(params: GetEWSBatchesParams): Promise<EWSBatchesResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());

    if (params.search) {
      queryParams.append('search', params.search);
    }

    if (params.status) {
      queryParams.append('status', params.status);
    }

    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }

    if (params.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }

    const response = await httpClient.get<EWSBatchesApiResponse>(
      `${this.basePath}/batches?${queryParams.toString()}`
    );

    if (response.data.status !== 'success' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch batches');
    }

    return response.data.data;
  }

  /**
   * Fetch the EWS config (view -> indicator mapping + display labels).
   * Backed by GET /v1/ews/config. The view list + per-view indicator membership
   * live in the ews_view_config table on FAS.
   *
   * @param batchId  when supplied, picks up per-batch overrides via the
   *                 3-level (tenant, batch) resolution hierarchy; when
   *                 omitted, returns the tenant default (or global if no
   *                 tenant row exists).
   */
  async getConfig(tenantId?: string, batchId?: string): Promise<EWSConfigResponse> {
    const queryParams = new URLSearchParams();
    if (tenantId) queryParams.append('tenant_id', tenantId);
    if (batchId) queryParams.append('batch_id', batchId);

    const qs = queryParams.toString();
    const response = await httpClient.get<EWSConfigApiResponse>(
      `${this.basePath}/config${qs ? '?' + qs : ''}`
    );

    if (response.data.status !== 'success' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch EWS config');
    }

    return response.data.data;
  }
}

// Export singleton instance
export const ewsApiService = new EWSApiService();

// Export class for testing
export { EWSApiService };
